import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    // Support both Paystack (reference) and legacy (session_id) params
    const reference = searchParams.get('reference') || searchParams.get('session_id');
    if (reference) {
      verifyPayment(reference);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const verifyPayment = async (reference) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/billing/verify/${reference}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid' || response.data.status === 'success') {
        setStatus('success');
        setPaymentDetails(response.data);
        
        // Update local storage with new plan
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
          user.plan = response.data.plan;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } else if (response.data.status === 'pending') {
        setStatus('pending');
        setPaymentDetails(response.data);
      } else {
        setStatus('error');
        setPaymentDetails(response.data);
      }
    } catch (error) {
      console.error('Failed to verify payment:', error);
      setStatus('error');
    }
  };

  const getPlanName = (plan) => {
    const names = {
      basic: 'Basic',
      pro: 'Professional',
      clinical: 'Clinical'
    };
    return names[plan] || plan;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="p-8 rounded-3xl border border-stone-100 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
              <h2 className="text-2xl font-serif text-primary-900 mb-2">Processing Payment</h2>
              <p className="text-stone-600">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-serif text-primary-900 mb-2">Payment Successful!</h2>
              <p className="text-stone-600 mb-6">
                Welcome to PlayFirst {getPlanName(paymentDetails?.plan)}!
              </p>
              
              {paymentDetails && (
                <div className="bg-stone-50 rounded-2xl p-4 mb-6 text-left">
                  <p className="text-sm text-stone-500">Plan</p>
                  <p className="font-medium text-primary-900 mb-2">{getPlanName(paymentDetails.plan)}</p>
                  <p className="text-sm text-stone-500">Amount</p>
                  <p className="font-medium text-primary-900">
                    {paymentDetails.currency?.toUpperCase()} {paymentDetails.amount || paymentDetails.amount_total}
                  </p>
                  {paymentDetails.extra_seats && parseInt(paymentDetails.extra_seats) > 0 && (
                    <>
                      <p className="text-sm text-stone-500 mt-2">Extra Seats</p>
                      <p className="font-medium text-primary-900">{paymentDetails.extra_seats}</p>
                    </>
                  )}
                  {paymentDetails.message && (
                    <p className="text-sm text-green-600 mt-2">{paymentDetails.message}</p>
                  )}
                </div>
              )}
              
              <Button
                onClick={() => navigate('/')}
                className="w-full rounded-full"
                data-testid="go-to-dashboard"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-amber-500 animate-spin" />
              <h2 className="text-2xl font-serif text-primary-900 mb-2">Payment Processing</h2>
              <p className="text-stone-600 mb-6">
                Your payment is being processed. This may take a moment.
              </p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="w-full rounded-full"
              >
                Check Again
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-serif text-primary-900 mb-2">Payment Issue</h2>
              <p className="text-stone-600 mb-6">
                There was a problem processing your payment. Please try again or contact support.
              </p>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/settings')}
                  className="w-full rounded-full"
                >
                  Try Again
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full rounded-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </Card>

        <div className="text-center mt-6">
          <div className="flex items-center justify-center gap-2 text-stone-400">
            <Heart className="w-4 h-4" />
            <span className="text-sm">PlayFirst</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
